import React, { useCallback, useEffect, useState } from 'react';
import { Box, Card, Container, Flex, Heading, Section, Text } from '@radix-ui/themes';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Lottie from 'react-lottie';
import LocalSort from 'components/common/localSort';
import PageTitle from 'components/layout/pageTitle';
import LocalMap from 'components/common/localMap';
import { MainSlider, PartnerSlider, BannerSlider } from 'components/common/slider';
import NoData from 'components/common/noData';
import Loading from 'components/common/loading'; // Loading 컴포넌트 import
// import noticeBanner from 'assets/images/common/main-notice-banner.png';
import promotionBanner from 'assets/images/common/main-promotion-banner.png';
import challengeBanner from 'assets/images/common/main-challenge-banner.png';
import promotionBanner02 from 'assets/images/common/main_special_banner_pc.png';
// import mainBannerAnimation from 'assets/images/common/main-banner-summer.json';
// import mainVisualSummer from 'assets/images/common/main-visual-summer.json';
import mainVisualAutumn from 'assets/images/common/main-visual-autumn.json';
import { localList } from 'app/constants';
import { selectUserSelectedLocalCode, setUserSelectedLocal, selectUserSelectedLocal } from 'store/slices/common';
import { instance } from 'api/axios.instance';
import './style.scss';
import Popup from 'components/popup';

// 메인 페이지
export default function Main() {
  const userSelectedLocal = useSelector(selectUserSelectedLocal);
  // hooks
  const dispatch = useDispatch();
  const userSelectedLocalCode = useSelector(selectUserSelectedLocalCode);
  const localCodeToNameMap = new Map(localList.map(local => [local.code, local.name]));
  // state
  const [allSliderList, setAllSliderList] = useState([]);
  const [filteredSliderList, setFilteredSliderList] = useState([]);
  const [noticeList, setNoticeList] = useState([]);
  const [eventList, setEventList] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('전체');
  const [regions, setRegions] = useState('전체');
  const [selectedMonth] = useState(new Date().getMonth() + 1);
  const [isLoading, setIsLoading] = useState(false);
  const baseURL = process.env.REACT_APP_API_BASE;

  // Lottie 옵션 설정
  const lottieOptions = {
    loop: true,
    autoplay: true,
    animationData: mainVisualAutumn, // 가을 테마 적용
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };

  // 메인 배너
  const banners = [
    {
      id: 1,
      to: '/promotion/detail/7729',
      title: '2025 SW미래채움 어울림데이 개최',
      src: promotionBanner02,
      alt: '2025 SW미래채움 어울림데이 개최'
    },
    {
      id: 2,
      to: '/promotion/detail/6890',
      title: 'SW미래채움 미래교육, 디지털을 채우다로 이동',
      src: promotionBanner,
      alt: 'SW미래채움 미래교육, 디지털을 채우다'
    }
  ];

  // 교육 & 홍보 게시판 조회 api - 전체 지역 데이터 조회
  const fetchSlider = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await instance(`${baseURL}/main/post?region=ALL&month=${selectedMonth}`, {
        method: 'GET'
      });

      if (response.data.status === 200) {
        const newData = response.data.data.posts;
        setAllSliderList(newData);
        // 초기 로드 시 현재 선택된 지역으로 필터링
        filterSliderData(newData, userSelectedLocal);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, [baseURL, selectedMonth, userSelectedLocal]);

  // 공지사항 목록 조회 api
  const fetchNotice = useCallback(async () => {
    const response = await instance(`${baseURL}/post/list`, {
      method: 'POST',
      data: {
        boardType: 'notice',
        region: 'ALL',
        searchType: '',
        search: '',
        pageNo: 1,
        pageSize: 3,
        inOrder: 'DESC'
      }
    });

    if (response.data.status === 200) {
      setNoticeList(response.data.data.posts);
    }
  }, [baseURL]);

  // 홍보자료 목록 조회 api
  const fetchEvent = useCallback(async () => {
    const response = await instance(`${baseURL}/post/list`, {
      method: 'POST',
      data: {
        boardType: 'promotion',
        region: 'ALL',
        searchType: '',
        search: '',
        pageNo: 1,
        pageSize: 3,
        inOrder: 'DESC'
      }
    });

    if (response.data.status === 200) {
      setEventList(response.data.data.posts);
    }
  }, [baseURL]);

  // 지역별 데이터 필터링 함수
  const filterSliderData = useCallback((data, selectedRegion) => {
    if (selectedRegion === '전체') {
      setFilteredSliderList(data);
    } else {
      const filtered = data.filter(item => item.region === selectedRegion);
      setFilteredSliderList(filtered);
    }
  }, []);

  // 메인 슬라이드 지역 선택 method
  const setRegion = async region => {
    const regionName = localCodeToNameMap.get(region);
    if (regionName) {
      dispatch(setUserSelectedLocal(regionName));
      setRegions(regionName);
      // 로컬 필터링 적용
      filterSliderData(allSliderList, regionName);
    }
  };
  // 지도 지역 선택 method
  const handleClick = (region, e) => {
    if (e.key === 'Enter' || e.type === 'click') {
      setSelectedRegion(region);
    }
  };

  const handleClose = val => {
    if (val) {
      const expires = new Date().setHours(new Date().getHours() + 24);
      localStorage.setItem('visited', expires);
    }
    setShowPopup(false);
  };

  useEffect(() => {
    const currentTime = new Date().getTime();
    const VISITED = localStorage.getItem('visited');
    const handleShowPopup = () => {
      if (!VISITED || currentTime > VISITED) {
        localStorage.removeItem('visited');
        setShowPopup(true);
      }
    };
    window.setTimeout(handleShowPopup, 500);
  }, []);

  // mount 시 실행
  useEffect(() => {
    fetchEvent();
    fetchNotice();
    fetchSlider();
  }, [fetchEvent, fetchNotice]);

  // 지역 변경 시 필터링 적용
  useEffect(() => {
    if (allSliderList.length > 0) {
      filterSliderData(allSliderList, userSelectedLocal);
    }
  }, [userSelectedLocal, allSliderList, filterSliderData]);

  return (
    <>
      {/* 홍보용 팝업 - 일회성 */}
      {showPopup && <Popup onClose={handleClose} />}
      <section className="visual main">
        <div className="lottie-container">
          <Lottie options={lottieOptions} height={220} isClickToPauseDisabled={true} />
        </div>
        <div className="text-wrap">
          <Heading as="h1" className="title4">
            소프트웨어 인재양성을 끌어갈
          </Heading>
          <div className="logo">
            <img src={require('assets/images/common/logo.svg').default} alt={'SW미래채움'} />
          </div>
        </div>
      </section>
      <Section p="0" className="main">
        <Container size="4">
          <Flex align="center">
            <LocalSort setRegion={setRegion} />
          </Flex>
        </Container>
        <Box className="slider-wrap">
          {/* 메인 슬라이더 */}
          <h3 className="hidden">{`${userSelectedLocal} 지역 정보`}</h3>
          {isLoading ? (
            <Loading style={{ height: '347px' }} />
          ) : filteredSliderList?.length > 0 ? (
            <MainSlider sliderList={filteredSliderList} />
          ) : (
            <NoData />
          )}
        </Box>
        <Container size="4">
          {/* 홍보, 공지 배너 */}
          <BannerSlider banners={banners} />
          <Box mb="100px">
            <PageTitle icon="star" title="홍보자료" />
            <Flex justify="between">
              {/* .chips.local에 지역명을 class로 입력시 해당 지역의 배지에 배경 색이 들어갑니다. 
                강원: gangwon, 경기: gyeonggi, 경남: gyeongnam, 경북: gyeongbuk, 광주: gwangju,
                대구: daegu, 울산: ulsan, 인천: incheon, 전남: jeonnam, 전북: jeonbuk,
                제주: jeju, 충남: chungnam , 충북: chungbuk
              */}
              {eventList?.map((item, index) => (
                <Box key={index}>
                  <Link to={`/promotion/detail/${item.postNo}`} title={`${item.title}로 이동`}>
                    <Card className="card image">
                      <Box className="header image-wrap">{item.thumbnail && <img src={item.thumbnail} alt={item.title} />}</Box>
                      <Box className="body">
                        <Text className={`chips local ${localList.find(data => data.name === item.region)?.en}`}>{item.region}</Text>
                        <Text as="p" className="sub-title title-2line">
                          {item.title}
                        </Text>
                        <Flex justify="between" className="utility">
                          <Text className="body2">{new Date(item.createDate).toISOString().split('T')[0]}</Text>
                          <Text className="body2 view">
                            {/* [웹접근성] 조회수 아이콘 before -> img 태그로 변경 */}
                            <img src={require('assets/images/icon/icon-view.svg').default} alt="조회수 아이콘" />
                            <span className="view-count">{item.viewCount}</span>
                          </Text>
                        </Flex>
                      </Box>
                    </Card>
                  </Link>
                </Box>
              ))}
            </Flex>
          </Box>
          <Box mb="100px">
            <Flex justify="between">
              <Box className="map-wrap">
                <LocalMap handleClick={handleClick} selectedRegion={selectedRegion} />
              </Box>
              <Box className="text-wrap">
                <PageTitle icon="local" title="지역교육을 한눈에!" />
                <Box className="card layer">
                  <Box className="body">
                    <Box className="image-wrap">
                      <img src={require('assets/images/common/logo.svg').default} alt={'SW미래채움'} />
                    </Box>
                    <Box className="text-wrap">
                      <Text as="p" className="title5">
                        SW 미래채움 {selectedRegion}센터
                      </Text>
                      <Text as="p" className="body2">
                        {selectedRegion}센터는 SW교육을 통해 지역의 SW 인재를 양성하고 있습니다.
                      </Text>
                    </Box>
                  </Box>
                  {selectedRegion !== '전체' && (
                    <Box className="footer">
                      <Link
                        onClick={() => dispatch(setUserSelectedLocal(selectedRegion))}
                        to={`/localOrganization`}
                        title="조직도로 이동"
                        className="sub-title"
                      >
                        조직도
                      </Link>
                      <Link
                        onClick={() => dispatch(setUserSelectedLocal(selectedRegion))}
                        to={`/localMap`}
                        title="찾아오시는 길로 이동"
                        className="sub-title"
                      >
                        찾아오시는 길
                      </Link>
                    </Box>
                  )}
                </Box>
              </Box>
            </Flex>
          </Box>
          <Box mb="140px">
            <Flex align="end">
              <Box className="notice-wrap">
                <PageTitle icon="bubble" title="공지사항" />
                <Box className="notice">
                  {noticeList?.map((item, index) => (
                    <Link key={index} to={`/notice/detail/${item.postNo}`} title={item.title} className="body1 font-bold">
                      <Text>
                        [{item.region}] {item.title}
                      </Text>
                      <Text className="regular date">{new Date(item.createDate).toISOString().split('T')[0]}</Text>
                    </Link>
                  ))}
                </Box>
              </Box>
              <Box className="buttons">
                <Link to="/intro" title="사업소개로 이동" className="title5">
                  사업 소개
                  <span className="image-wrap">
                    <img src={require('assets/images/common/img-book.svg').default} alt="사업 소개" />
                  </span>
                </Link>
                <Link to="/ci" title="CI 소개로 이동" className="title5">
                  CI 소개
                  <span className="image-wrap">
                    <img src={require('assets/images/common/logo-sw.svg').default} alt="CI 소개" />
                  </span>
                </Link>
              </Box>
            </Flex>
          </Box>
        </Container>
      </Section>
      <Section p="0" className="partners">
        <Container size="4">
          <Flex justify="between" align="center">
            <Text as="p" className="sub-title">
              함께하는 기관
            </Text>
            <PartnerSlider />
          </Flex>
        </Container>
      </Section>
    </>
  );
}
